/**
 * Locale experience primitives shared by every Kreate.love application.
 *
 * These helpers deliberately use the platform Intl implementation instead of
 * introducing an i18n runtime. Text translation remains a later phase; this
 * module establishes deterministic locale, number, currency, date and time
 * presentation first.
 */

export interface LocaleExperienceInput {
  defaultLocale?: string | null;
  supportedLocales?: readonly (string | null | undefined)[] | null;
  timeZone?: string | null;
  preferredLocales?: readonly (string | null | undefined)[] | null;
}

export interface LocaleExperience {
  locale: string;
  defaultLocale: string;
  supportedLocales: readonly string[];
  timeZone: string;
}

const FALLBACK_LOCALE = 'en-US';
const FALLBACK_TIME_ZONE = 'UTC';

/** Return an Intl-canonical BCP 47 locale tag, or null when invalid. */
export function canonicalizeLocaleTag(value: string | null | undefined): string | null {
  const candidate = value?.trim();
  if (!candidate) return null;

  try {
    return Intl.getCanonicalLocales(candidate)[0] ?? null;
  } catch {
    return null;
  }
}

/** Return a valid IANA time-zone identifier, falling back to UTC. */
export function normalizeTimeZone(value: string | null | undefined): string {
  const candidate = value?.trim();
  if (!candidate) return FALLBACK_TIME_ZONE;

  try {
    new Intl.DateTimeFormat(FALLBACK_LOCALE, { timeZone: candidate }).format(new Date(0));
    return candidate;
  } catch {
    return FALLBACK_TIME_ZONE;
  }
}

/** Canonicalize, de-duplicate and guarantee inclusion of the default locale. */
export function normalizeSupportedLocales(
  supportedLocales: readonly (string | null | undefined)[] | null | undefined,
  defaultLocale: string | null | undefined,
): readonly string[] {
  const normalizedDefault = canonicalizeLocaleTag(defaultLocale) ?? FALLBACK_LOCALE;
  const values = [normalizedDefault, ...(supportedLocales ?? [])];
  const seen = new Set<string>();
  const normalized: string[] = [];

  for (const value of values) {
    const locale = canonicalizeLocaleTag(value);
    if (!locale) continue;
    const key = locale.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    normalized.push(locale);
  }

  return normalized.length > 0 ? normalized : [FALLBACK_LOCALE];
}

/**
 * Choose a supported locale from explicit/browser preferences.
 *
 * Resolution order:
 * 1. Exact canonical locale match.
 * 2. Same-language match (for example `pl` -> `pl-PL`).
 * 3. Canonical default locale.
 */
export function resolvePreferredLocale(
  preferredLocales: readonly (string | null | undefined)[] | null | undefined,
  supportedLocales: readonly string[],
  defaultLocale: string,
): string {
  const normalizedSupported = normalizeSupportedLocales(supportedLocales, defaultLocale);
  const normalizedDefault =
    canonicalizeLocaleTag(defaultLocale) ?? normalizedSupported[0] ?? FALLBACK_LOCALE;

  for (const preference of preferredLocales ?? []) {
    const preferred = canonicalizeLocaleTag(preference);
    if (!preferred) continue;

    const exact = normalizedSupported.find(
      (supported) => supported.toLowerCase() === preferred.toLowerCase(),
    );
    if (exact) return exact;

    const preferredLanguage = preferred.split('-')[0]?.toLowerCase();
    const languageMatch = normalizedSupported.find(
      (supported) => supported.split('-')[0]?.toLowerCase() === preferredLanguage,
    );
    if (languageMatch) return languageMatch;
  }

  return (
    normalizedSupported.find(
      (supported) => supported.toLowerCase() === normalizedDefault.toLowerCase(),
    ) ??
    normalizedSupported[0] ??
    FALLBACK_LOCALE
  );
}

/** Resolve one safe, deterministic locale experience. */
export function resolveLocaleExperience(input: LocaleExperienceInput): LocaleExperience {
  const defaultLocale = canonicalizeLocaleTag(input.defaultLocale) ?? FALLBACK_LOCALE;
  const supportedLocales = normalizeSupportedLocales(input.supportedLocales, defaultLocale);
  const locale = resolvePreferredLocale(input.preferredLocales, supportedLocales, defaultLocale);

  return {
    locale,
    defaultLocale,
    supportedLocales,
    timeZone: normalizeTimeZone(input.timeZone),
  };
}

export function formatLocalizedNumber(
  value: number | null | undefined,
  locale: string,
  options?: Intl.NumberFormatOptions,
): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '';

  try {
    return new Intl.NumberFormat(canonicalizeLocaleTag(locale) ?? FALLBACK_LOCALE, options).format(
      value,
    );
  } catch {
    return String(value);
  }
}

/** Format integer cents as localized currency without changing the currency. */
export function formatLocalizedCurrency(
  cents: number | null | undefined,
  currency: string,
  locale: string,
  options?: Omit<Intl.NumberFormatOptions, 'style' | 'currency'>,
): string {
  if (cents === null || cents === undefined || !Number.isFinite(cents)) return '';

  const normalizedCurrency = currency.trim().toUpperCase();
  try {
    return new Intl.NumberFormat(canonicalizeLocaleTag(locale) ?? FALLBACK_LOCALE, {
      ...options,
      style: 'currency',
      currency: normalizedCurrency,
    }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${normalizedCurrency}`;
  }
}

type LocalizedDateInput = Date | string | number | null | undefined;

function toValidDate(value: LocalizedDateInput): Date | null {
  if (value === null || value === undefined || value === '') return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatLocalizedDateValue(
  value: LocalizedDateInput,
  locale: string,
  timeZone: string,
  options: Intl.DateTimeFormatOptions,
): string {
  const date = toValidDate(value);
  if (!date) return '';

  try {
    return new Intl.DateTimeFormat(canonicalizeLocaleTag(locale) ?? FALLBACK_LOCALE, {
      ...options,
      timeZone: normalizeTimeZone(timeZone),
    }).format(date);
  } catch {
    return date.toISOString();
  }
}

export function formatLocalizedDate(
  value: LocalizedDateInput,
  locale: string,
  timeZone: string,
  options: Intl.DateTimeFormatOptions = { dateStyle: 'medium' },
): string {
  return formatLocalizedDateValue(value, locale, timeZone, options);
}

export function formatLocalizedTime(
  value: LocalizedDateInput,
  locale: string,
  timeZone: string,
  options: Intl.DateTimeFormatOptions = { timeStyle: 'short' },
): string {
  return formatLocalizedDateValue(value, locale, timeZone, options);
}

export function formatLocalizedDateTime(
  value: LocalizedDateInput,
  locale: string,
  timeZone: string,
  options: Intl.DateTimeFormatOptions = { dateStyle: 'medium', timeStyle: 'short' },
): string {
  return formatLocalizedDateValue(value, locale, timeZone, options);
}
